/**
 * useSessionSocket Hook
 *
 * React hook for managing socket-based session tracking.
 * Provides easy access to session events and actions.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  initializeSocket,
  registerSession,
  extendSession,
  logoutSession,
  logoutAllSessions,
  getActiveSessions,
  terminateSession,
  subscribeToEvent,
  isConnected,
  getSocketId,
  disconnectSocket,
} from '../services/socketService';
import type {
  SessionExpiryWarningEvent,
  SessionExpiredEvent,
  DuplicateLoginEvent,
  ForceLogoutEvent,
  SessionExtendedEvent,
  UserSession
} from '../services/socketService';

interface SessionSocketState {
  isConnected: boolean;
  socketId: string | null;
  sessionRegistered: boolean;
  isDuplicateSession: boolean;
  expiryWarning: SessionExpiryWarningEvent | null;
  isSessionExpired: boolean;
  forceLogoutReason: ForceLogoutEvent | null;
  duplicateLoginInfo: DuplicateLoginEvent | null;
}

interface UseSessionSocketReturn extends SessionSocketState {
  // Actions
  registerUserSession: (userId: string, username: string, tokenExpiry: number) => void;
  extendUserSession: (newTokenExpiry: number) => void;
  logout: () => void;
  logoutAll: () => void;
  getMyActiveSessions: () => Promise<UserSession[]>;
  terminateOtherSession: (socketId: string) => void;
  disconnect: () => void;
  // State management
  clearExpiryWarning: () => void;
  clearDuplicateLoginInfo: () => void;
  clearForceLogout: () => void;
}

export function useSessionSocket(): UseSessionSocketReturn {
  const [state, setState] = useState<SessionSocketState>({
    isConnected: false,
    socketId: null,
    sessionRegistered: false,
    isDuplicateSession: false,
    expiryWarning: null,
    isSessionExpired: false,
    forceLogoutReason: null,
    duplicateLoginInfo: null
  });

  const unsubscribersRef = useRef<(() => void)[]>([]);

  // Initialize socket and set up event listeners
  useEffect(() => {
    const socket = initializeSocket();

    // Update connection state
    setState(prev => ({
      ...prev,
      isConnected: isConnected(),
      socketId: getSocketId() || null
    }));

    // Subscribe to connection events
    const unsubConnect = subscribeToEvent('connect', (data: { socketId: string }) => {
      setState(prev => ({
        ...prev,
        isConnected: true,
        socketId: data.socketId
      }));
    });

    const unsubDisconnect = subscribeToEvent('disconnect', () => {
      setState(prev => ({
        ...prev,
        isConnected: false,
        sessionRegistered: false
      }));
    });

    // Subscribe to session events
    const unsubRegistered = subscribeToEvent('session:registered', (data: { isDuplicate: boolean }) => {
      setState(prev => ({
        ...prev,
        sessionRegistered: true,
        isDuplicateSession: data.isDuplicate
      }));
    });

    const unsubExpiryWarning = subscribeToEvent<SessionExpiryWarningEvent>('session:expiry-warning', (data) => {
      setState(prev => ({
        ...prev,
        expiryWarning: data
      }));
    });

    const unsubExpired = subscribeToEvent<SessionExpiredEvent>('session:expired', () => {
      setState(prev => ({
        ...prev,
        isSessionExpired: true,
        sessionRegistered: false
      }));
    });

    const unsubDuplicateLogin = subscribeToEvent<DuplicateLoginEvent>('session:duplicate-login', (data) => {
      setState(prev => ({
        ...prev,
        duplicateLoginInfo: data
      }));
    });

    const unsubForceLogout = subscribeToEvent<ForceLogoutEvent>('session:force-logout', (data) => {
      setState(prev => ({
        ...prev,
        forceLogoutReason: data,
        sessionRegistered: false
      }));
    });

    const unsubExtended = subscribeToEvent<SessionExtendedEvent>('session:extended', () => {
      setState(prev => ({
        ...prev,
        expiryWarning: null
      }));
    });

    // Store unsubscribers
    unsubscribersRef.current = [
      unsubConnect,
      unsubDisconnect,
      unsubRegistered,
      unsubExpiryWarning,
      unsubExpired,
      unsubDuplicateLogin,
      unsubForceLogout,
      unsubExtended
    ];

    // Cleanup on unmount
    return () => {
      unsubscribersRef.current.forEach(unsub => unsub());
      unsubscribersRef.current = [];
    };
  }, []);

  // Actions
  const registerUserSession = useCallback((userId: string, username: string, tokenExpiry: number) => {
    registerSession({ userId, username, tokenExpiry });
  }, []);

  const extendUserSession = useCallback((newTokenExpiry: number) => {
    extendSession(newTokenExpiry);
  }, []);

  const logout = useCallback(() => {
    logoutSession();
    setState(prev => ({
      ...prev,
      sessionRegistered: false
    }));
  }, []);

  const logoutAll = useCallback(() => {
    logoutAllSessions();
    setState(prev => ({
      ...prev,
      sessionRegistered: false
    }));
  }, []);

  const getMyActiveSessions = useCallback(async (): Promise<UserSession[]> => {
    try {
      const result = await getActiveSessions();
      return result.sessions;
    } catch (error) {
      console.error('Failed to get active sessions:', error);
      return [];
    }
  }, []);

  const terminateOtherSession = useCallback((socketId: string) => {
    terminateSession(socketId);
  }, []);

  const disconnect = useCallback(() => {
    disconnectSocket();
    setState({
      isConnected: false,
      socketId: null,
      sessionRegistered: false,
      isDuplicateSession: false,
      expiryWarning: null,
      isSessionExpired: false,
      forceLogoutReason: null,
      duplicateLoginInfo: null
    });
  }, []);

  // State management
  const clearExpiryWarning = useCallback(() => {
    setState(prev => ({ ...prev, expiryWarning: null }));
  }, []);

  const clearDuplicateLoginInfo = useCallback(() => {
    setState(prev => ({ ...prev, duplicateLoginInfo: null }));
  }, []);

  const clearForceLogout = useCallback(() => {
    setState(prev => ({ ...prev, forceLogoutReason: null }));
  }, []);

  return {
    // State
    ...state,
    // Actions
    registerUserSession,
    extendUserSession,
    logout,
    logoutAll,
    getMyActiveSessions,
    terminateOtherSession,
    disconnect,
    // State management
    clearExpiryWarning,
    clearDuplicateLoginInfo,
    clearForceLogout
  };
}

export default useSessionSocket;
